import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Printer } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { money, fmtDate } from "@/lib/constants";
import { useCamp } from "@/context/SettingsContext";
import PrintLogo from "@/components/print/PrintLogo";
import PrintFooter from "@/components/print/PrintFooter";
import PayQR from "@/components/print/PayQR";
import { Button } from "@/components/ui/button";
import DemoStamp from "@/components/DemoStamp";

export default function PrintSale() {
  const { t, lang } = useLang();
  const { camp } = useCamp();
  const [params] = useSearchParams();
  const id = params.get("sale");
  const [sale, setSale] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/sales/${id}`);
      setSale(data);
      try { await api.post(`/sales/${id}/printed`); } catch (e) { console.warn("Afdrukstatus bewaren mislukt", e?.message); }
    })();
  }, [id]);

  useEffect(() => {
    if (!sale) return;
    const timer = setTimeout(() => window.print(), 500);
    return () => clearTimeout(timer);
  }, [sale]);

  if (!sale) return <div className="p-8 text-sm text-muted-foreground">…</div>;

  const bt = sale.bill_to || {};
  const addr = [bt.street, bt.house_number].filter(Boolean).join(" ");
  const cityLine = [bt.postal_code, bt.city].filter(Boolean).join(" ");

  return (
    <div className="print-page bg-white text-slate-900 mx-auto max-w-[800px] p-8 print:p-0" data-testid="print-sale">
      <DemoStamp />
      <div className="flex justify-end print:hidden mb-3">
        <Button size="sm" onClick={() => window.print()} data-testid="print-sale-now"><Printer className="h-4 w-4 mr-1" />{t("print")}</Button>
      </div>

      {!camp.vat_number && (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-900 print:hidden" data-testid="print-sale-no-vat">
          {t("no_vat_number_warning")}
        </div>
      )}

      {/* Kop: logo links boven, camping-gegevens rechts */}
      <div className="flex items-start justify-between gap-8 border-b-2 border-slate-800 pb-4">
        <PrintLogo camp={camp} testid="print-sale-logo" />
        <div className="text-right text-[11px] leading-snug">
          <p className="font-display font-extrabold text-base text-slate-900">{camp.camp_name}</p>
          {camp.street && <p>{camp.street}</p>}
          {camp.postal_city && <p>{camp.postal_city}</p>}
          {camp.country && <p>{camp.country}</p>}
          {camp.phone && <p>{camp.phone}</p>}
          {camp.email && <p>{camp.email}</p>}
          {camp.website && <p>{camp.website}</p>}
          {camp.vat_number && <p className="font-semibold mt-1">{t("setup_vat_number")}: {camp.vat_number}</p>}
        </div>
      </div>

      {/* Facturatiegegevens bovenaan */}
      <div className="mt-6 flex items-start justify-between gap-8">
        <div className="text-[12px] leading-relaxed" data-testid="print-sale-billto">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1">{t("bill_to")}</p>
          <p className="font-bold text-sm">{bt.name || sale.guest_name}</p>
          {addr && <p>{addr}</p>}
          {cityLine && <p>{cityLine}</p>}
          {bt.country && <p>{bt.country}</p>}
          {bt.vat_number && <p className="mt-1">{t("setup_vat_number")}: {bt.vat_number}</p>}
          {bt.email && <p>{bt.email}</p>}
        </div>
        <table className="text-[12px]">
          <tbody>
            <tr><td className="pr-4 text-slate-500">{t("invoice_number")}</td><td className="font-bold tabular-nums" data-testid="print-sale-number">{sale.number || sale.id.slice(0, 8)}</td></tr>
            <tr><td className="pr-4 text-slate-500">{t("date")}</td><td className="tabular-nums">{fmtDate(sale.created_at)}</td></tr>
            {sale.pitch_label && <tr><td className="pr-4 text-slate-500">{t("pitch")}</td><td className="font-semibold">{sale.pitch_label}</td></tr>}
            <tr><td className="pr-4 text-slate-500">{t("payment")}</td><td>{t(sale.payment === "cash" ? "pay_cash" : "pay_account")}</td></tr>
          </tbody>
        </table>
      </div>

      {/* Artikellijnen */}
      <table className="w-full mt-6 text-[12px]" data-testid="print-sale-lines">
        <thead>
          <tr className="border-y border-slate-300 text-[10px] uppercase tracking-wider text-slate-600">
            <th className="text-left py-1.5">{t("art_code")}</th>
            <th className="text-left py-1.5">{t("description")}</th>
            <th className="text-right py-1.5">{t("qty")}</th>
            <th className="text-right py-1.5">{t("unit_price")}</th>
            <th className="text-right py-1.5">{t("vat")}</th>
            <th className="text-right py-1.5">{t("total")}</th>
          </tr>
        </thead>
        <tbody>
          {(sale.lines || []).map((l, i) => (
            <tr key={i} className="border-b border-slate-100">
              <td className="py-1.5 font-mono text-[10px]">{l.code}</td>
              <td className="py-1.5">
                {l.description}
                {l.category && <span className="block text-[10px] uppercase tracking-wider text-slate-500">{l.category}</span>}
              </td>
              <td className="py-1.5 text-right tabular-nums">{l.qty}</td>
              <td className="py-1.5 text-right tabular-nums">{money(l.unit_price)}</td>
              <td className="py-1.5 text-right tabular-nums">{l.vat_rate}%</td>
              <td className="py-1.5 text-right tabular-nums font-semibold">{money(l.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totalen + BTW-opsplitsing */}
      <div className="mt-4 flex justify-end">
        <table className="text-[12px] min-w-[16rem]">
          <tbody>
            <tr><td className="text-slate-500 pr-6 py-0.5">{t("subtotal")} ({t("excl_vat")})</td><td className="text-right tabular-nums font-semibold" data-testid="print-sale-subtotal">{money(sale.subtotal)}</td></tr>
            {(sale.vat_summary || []).map((v, i) => (
              <tr key={i}><td className="text-slate-500 pr-6 py-0.5">{t("vat")} {v.rate}% ({t("vat_of")} {money(v.base)})</td><td className="text-right tabular-nums">{money(v.vat)}</td></tr>
            ))}
            {!sale.vat_summary?.length && <tr><td className="text-slate-500 pr-6 py-0.5">{t("vat")}</td><td className="text-right tabular-nums">{money(sale.vat_amount)}</td></tr>}
            <tr className="border-t-2 border-slate-800"><td className="pr-6 pt-1.5 font-bold">{t("total")}</td><td className="text-right pt-1.5 font-display font-extrabold text-base tabular-nums" data-testid="print-sale-total">{money(sale.total)}</td></tr>
          </tbody>
        </table>
      </div>

      {sale.note && <p className="mt-6 text-[11px] text-slate-600">{sale.note}</p>}
      {sale.seller_name && (
        <p className="mt-2 text-[11px] text-slate-500" data-testid="print-sale-seller">
          {t("seller")}: {sale.seller_name}{sale.seller_number ? ` (nr. ${sale.seller_number})` : ""}
        </p>
      )}

      {sale.payment !== "cash" && (
        <div className="mt-6">
          <PayQR camp={camp} lang={lang} amount={sale.total} reference={sale.number || ""} />
        </div>
      )}

      <PrintFooter camp={camp} lang={lang} vatLabel={t("setup_vat_number")} note={t("invoice_footer_note")} />
    </div>
  );
}
